<template>
  <div class="overflow-hidden">
    <PageTitle
      :small="true" 
      :subTitle="`Funcionário: ${collab?.name}`"
    >
      Estatisticas do colaborador
    </PageTitle>

    <main class="">
      <!-- data -->
      <div class="calendar-container -mt-10 pt-2">
        <div class="calendar-header">
          <button @click="prevMonth" class="nav-button ml-2">
            <ChevronDownIcon class="h-7 w-7 text-gray-400 rotate-90" />
          </button>
          <span class="month-year">{{ monthYear }}</span>
          <button @click="nextMonth" class="nav-button mr-2">
            <ChevronDownIcon class="h-7 w-7 text-gray-400 -rotate-90" />
          </button>
        </div>
      </div>
  
      <div class="overflow-x-hidden">
        <!-- cards -->
        <section>
          <div
            class="w-full mb-1"
            :class="[
              isMobile
                ? 'grid gap-4 gap-y-2 text-sm grid-cols-2'
                : 'flex justify-center font-semibold',
            ]"
          >
            <StaticsCard
              v-for="(item, index) in cards" :key="index"
              :label="item.label"
              :value="item.count"
              
              :hTraba="item.label == 'Horas trabalhadas' ? true : false"
              :hDeved="item.label == 'Horas devedoras' ? true : false"
              :hExtras="item.label == 'Horas extras' ? true : false"
              :faltas="item.label == 'Faltas / Atestados' ? true : false"
            />
          </div>
        </section>
  
        <!-- relatório -->
        <section class="m-3">
          <div
            @click="openOption('ponto')"
            class="cursor-pointer md:col-span-5"
          >
            <div
              class="flex justify-between items-center bg-gray-100 rounded p-1"
            >
              <label class="font-medium text-lg text-gray-800 p-1 -ml-1">
                RELATÓRIO DE PONTOS
              </label>
              <component
                :is="PhCaretUp"
                class="w-6 h-6 transition-all"
                :class="{ 'rotate-180': pontoOption }"
              ></component>
            </div>
          </div>
  
          <!-- form -->
          <Transition mode="out-in">
            <div v-show="pontoOption">
              <Form
                id="form-fields"
                :descriptionTitle="'Gere um relatório dos horarios desse colaborador'"
              >
                <div class="w-full flex justify-center mt-4">
                  <Create
                    @click="exportExcel"
                    :icon="PhFileXls"
                    :label="'Gerar'"
                  />
                </div>
              </Form>
            </div>
          </Transition>
        </section>
  
      </div>
    </main>

    <FloatBackPage />
  </div>  
</template>

<script setup>
import FloatBackPage from "@/components/Buttons/FloatBackPage.vue";
import StaticsCard from '../components/helpers/StaticsCard.vue';
import PageTitle from '@/components/Titles/PageTitle.vue';
import Create from '@/components/Buttons/Create.vue';
import homeService, { PATH } from '../services/home.service';
import Form from './../components/Form.vue'
import { CreateExportFile } from "@/utils/reports/generate-blob";
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { PhCaretUp, PhFileXls } from '@phosphor-icons/vue';
import { showToast as toast } from '@/helpers/UseToasts';
import * as yup from '../../../validator'
import { onBeforeMount, ref } from 'vue';
import { useRoute } from 'vue-router';
import { dataHome } from '../store';
import { storeApp } from '@/store';
import { computed } from 'vue';
import { cleanAllFormFields, setFormFieldsError, validateFormField } from "@/helpers/formFieldsHandler";

const store = dataHome;
const pontoOption = ref(true);
const daysInMonth = ref([]);
const id = useRoute().params.id;
const currentDate = ref(new Date());
const monthsFilter = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const collab = computed(() => store.getters.getCollab)
const cards = computed(() => store.getters.getCards)
const isMobile = computed(() => window.innerWidth < 768);
const monthYear = computed(() => {
  const month = currentDate.value.getMonth();
  const year = currentDate.value.getFullYear();
  return `${monthsFilter[month]} ${year}`;
});

const schema = yup.object().shape({
  form: yup.object().shape({
    period: yup.string().required('É necessário informar um período').label('Periodo')
  })
})

async function getStatistics (params) {
  storeApp.state.loading = true
  
  await homeService.getDataCard(id, params?.month, params?.year)
    .then(res => {
      store.dispatch('setCards', res.data.cards);
    }).catch(err => {
      storeApp.state.loading = false

      return toast(
        'Aviso',
        'Algo deu errado ao buscar horarios do colaborador',
        'error'
      )
    })

  storeApp.state.loading = false
}

async function getDataCollab () {
  await homeService.getDataByCollabId(id)
    .then(res => {
      store.dispatch('setDataCollab', res.data);
    }).catch(err => {
      return toast(
        'Aviso',
        'Algo deu errado ao buscar dados do colaborador',
        'error'
      )
    })
}

async function nextMonth() {
  const month = currentDate.value.getMonth();
  if (month === 11) {
    currentDate.value = new Date(currentDate.value.getFullYear() + 1, 0, 1);
  } else {
    currentDate.value = new Date(currentDate.value.getFullYear(), month + 1, 1);
  }

  Promise.allSettled([
    await getStatistics({
      month: currentDate.value.getMonth() + 1,
      year: currentDate.value.getFullYear(),
    })
  ])

  updateDaysInMonth();
}

async function prevMonth() {
  const month = currentDate.value.getMonth();
  if (month === 0) {
    currentDate.value = new Date(currentDate.value.getFullYear() - 1, 11, 1);
  } else {
    currentDate.value = new Date(currentDate.value.getFullYear(), month - 1, 1);
  }

  Promise.allSettled([
    await getStatistics({
      month: currentDate.value.getMonth() + 1,
      year: currentDate.value.getFullYear(),
    })
  ])

  updateDaysInMonth();
}

function updateDaysInMonth() {
  const year = currentDate.value.getFullYear();
  const month = currentDate.value.getMonth();
  const days = [];

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();

  for (let i = 0; i < (firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1); i++) {
    days.push({ date: null, label: "" });
  }

  for (let day = 1; day <= totalDays; day++) {
    days.push({
      date: new Date(year, month, day),
      label: day,
    });
  }

  daysInMonth.value = days;
}

function openOption(option) {
  if (option == 'ponto') {
    pontoOption.value = ! pontoOption.value
  }
}

async function exportExcel() {
  storeApp.state.loading = true;

  cleanAllFormFields()
  const validate = (await validateFormField(schema, store.state))

  if (validate && validate.length > 0) {
    setFormFieldsError(validate)
    storeApp.state.loading = false;
    return
  }

  let query = `period=${store.state.form.period}&type=${store.state.form.pontoType ?? 'GERAL'}`

  if(store.state.form.period == 'PERSONALIZADO')
    query = query.concat(`&date_start=${store.state.form.date_start}&date_end=${store.state.form.date_end}`)

  const url = `${process.env.VUE_APP_API_URL}${PATH}export/${id}?${query}`
  const report = new CreateExportFile(url)

  return await report.generatePontoReportByCollabId(collab.value?.name, store.state.form.period).finally(() => {
    storeApp.state.loading = false
  })

}

onBeforeMount(async () => {
  storeApp.state.loading = true
  store.state.form.user_id = useRoute().params.id
  await getDataCollab();
  storeApp.state.loading = false
})

</script>

<style scoped lang="scss">
.calendar-container {
  margin: -10px auto 20px;
  padding-bottom: -10px;
  border-radius: 8px;
  width: 100%;
  max-width: 320px;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-weight: bold;
}

.nav-button {
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
}
</style>
